import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

gsap.registerPlugin(ScrollTrigger);

export function SmoothScroll() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const lenisRef = useRef<Lenis | null>(null);
  const previousPath = useRef<string | null>(null);

  useLayoutEffect(() => {
    const lenis = new Lenis({
      duration: 0.85,
      smoothWheel: true,
      syncTouch: false,
      autoToggle: true,
      stopInertiaOnNavigate: true,
      prevent: (node) => Boolean(node.closest('[data-lenis-prevent], dialog[open], .mobile-nav')),
    });
    lenisRef.current = lenis;
    lenis.on('scroll', ScrollTrigger.update);

    const update = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(update);

    const syncScrollLock = () => {
      if (document.body.style.overflow === 'hidden') lenis.stop();
      else lenis.start();
    };
    const lockObserver = new MutationObserver(syncScrollLock);
    lockObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    syncScrollLock();

    const onAnchorClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
      if (!anchor || anchor.target || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash) return;
      const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
      if (!target) return;
      event.preventDefault();
      if (url.hash === location.hash) lenis.scrollTo(target);
      else navigate({ hash: url.hash });
    };
    document.addEventListener('click', onAnchorClick, true);

    return () => {
      document.removeEventListener('click', onAnchorClick, true);
      lockObserver.disconnect();
      gsap.ticker.remove(update);
      lenis.off('scroll', ScrollTrigger.update);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [navigate]);

  useLayoutEffect(() => {
    const lenis = lenisRef.current;
    if (!lenis) return;

    const samePage = previousPath.current === pathname;
    previousPath.current = pathname;

    if (!hash) {
      if (!samePage) lenis.scrollTo(0, { immediate: true, force: true });
      return;
    }

    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (target) lenis.scrollTo(target, { immediate: !samePage, force: true });
      ScrollTrigger.refresh();
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return null;
}
